/**
 * Pack Detail Page -- dedicated route for bundle/pack pages.
 *
 * Renders a hero image, marketing content, and the BundleBuilder for adding
 * the pack to the cart. Uses the products-go pack details endpoint and
 * storefront-bff → tss-svc-pricing pack-price resolver.
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { usePackPage } from '@/lib/hooks/use-pack';
import { useAddPackToCart } from '@/lib/hooks/use-add-pack-to-cart';
import { BundleBuilder } from '@/components/organisms/BundleBuilder/BundleBuilder';
import { useIntl } from '@/lib/intl';
import { useTranslation } from '@/lib/i18n/client';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space.lg} ${theme.space.md}`};
  display: grid;
  grid-template-columns: 52fr 48fr;
  gap: ${({ theme }) => theme.space['3xl']};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.lg};
    padding: ${({ theme }) => `${theme.space.md} ${theme.space.sm}`};
  }
`;

const HeroColumn = styled.div`
  position: sticky;
  top: ${({ theme }) => theme.space.md};
  height: fit-content;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    position: relative;
    top: 0;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const PackTitle = styled.h1`
  font-size: 32px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  line-height: 1.2;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  line-height: 1.5;
`;

const ErrorWrapper = styled.div`
  padding: 2rem;
  text-align: center;
`;

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: underline;
`;

export function PackPage() {
  const { slug } = useParams<{ slug: string }>();
  const { localizedPath, countryConfig } = useIntl();
  const { t } = useTranslation('pdp');

  const { data, isLoading } = usePackPage({
    slug,
    currency: countryConfig.currency.code,
    country: countryConfig.countryCode,
  });
  const pack = data?.pack;
  const packPrice = data?.pricing ?? null;
  const { addPack, isAdding, error: addPackError } = useAddPackToCart();
  const [heroVariantImage, setHeroVariantImage] = useState<string | undefined>();

  if (!slug) {
    return (
      <ErrorWrapper>
        <p>Invalid pack URL</p>
        <BackLink to={localizedPath('/products')}>← Back to Products</BackLink>
      </ErrorWrapper>
    );
  }

  if (isLoading) {
    return (
      <ErrorWrapper>
        <p>{t('pack.loading', 'Loading pack...')}</p>
      </ErrorWrapper>
    );
  }

  if (!pack) {
    return (
      <ErrorWrapper>
        <p>{t('pack.notFound', 'Pack not found')}</p>
        <BackLink to={localizedPath('/products')}>← Back to Products</BackLink>
      </ErrorWrapper>
    );
  }

  const heroSrc =
    heroVariantImage || pack.image_url || pack.items[0]?.product?.images?.[0]?.src;

  return (
    <PageContainer>
      <HeroColumn>
        {heroSrc ? <HeroImage src={heroSrc} alt={pack.name} /> : null}
      </HeroColumn>

      <DetailsColumn>
        <PackTitle>{pack.name}</PackTitle>
        {pack.description_html ? (
          <Description dangerouslySetInnerHTML={{ __html: pack.description_html }} />
        ) : pack.description ? (
          <Description>{pack.description}</Description>
        ) : null}

        <BundleBuilder
          pack={pack}
          price={packPrice || null}
          isAdding={isAdding}
          onAddToCart={(selections) => addPack(pack.id, selections)}
          onFirstSlotVariantChange={(variantId) => {
            if (!variantId || !pack.items[0]?.product) {
              setHeroVariantImage(undefined);
              return;
            }
            const variant = pack.items[0].product.variants.find((v) => v.id === variantId);
            const imageId = variant?.image_id;
            const src = imageId
              ? pack.items[0].product.images.find((img) => img.id === imageId)?.src
              : pack.items[0].product.images[0]?.src;
            setHeroVariantImage(src);
          }}
        />
        {addPackError ? (
          <AddPackError role="alert">
            {t('pack.addError', "Couldn't add this pack to your cart. Please try again.")}
          </AddPackError>
        ) : null}
      </DetailsColumn>
    </PageContainer>
  );
}

const AddPackError = styled.div`
  margin-top: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.sm};
  border: 1px solid ${({ theme }) => theme.colors.error ?? '#d33'};
  border-radius: ${({ theme }) => theme.radius.regular};
  color: ${({ theme }) => theme.colors.error ?? '#d33'};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
`;
